import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../config/firebase';

// Module-level cache — single subscription shared across all components.
let cachedDistricts = null;
let listeners = [];
let unsubscribeSnapshot = null;
let unsubscribeAuth = null;

function startSnapshotListener() {
  if (unsubscribeSnapshot) return;

  const q = query(collection(db, 'districts'), orderBy('name', 'asc'));
  unsubscribeSnapshot = onSnapshot(
    q,
    (snap) => {
      const districts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      cachedDistricts = districts;
      listeners.forEach((fn) => fn(districts));
    },
    (err) => {
      console.error('useDistricts: Firestore error', err);
      unsubscribeSnapshot = null;
      cachedDistricts = [];
      listeners.forEach((fn) => fn([]));
    }
  );
}

function subscribe(callback) {
  listeners.push(callback);

  if (!unsubscribeAuth) {
    // Gate the first query on Firebase Auth's own session-restore instead of
    // firing on mount: starting the listener before auth persistence finishes
    // loading can race ahead and get a spurious permission-denied that then
    // sticks empty for the rest of the tab session (no automatic retry).
    unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        startSnapshotListener();
      } else if (cachedDistricts === null) {
        cachedDistricts = [];
        listeners.forEach((fn) => fn([]));
      }
    });
  } else if (cachedDistricts !== null) {
    callback(cachedDistricts);
  }

  return () => {
    listeners = listeners.filter((fn) => fn !== callback);
    if (listeners.length === 0) {
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
        unsubscribeSnapshot = null;
      }
      if (unsubscribeAuth) {
        unsubscribeAuth();
        unsubscribeAuth = null;
      }
      cachedDistricts = null;
    }
  };
}

/**
 * Returns all districts from Firestore, ordered by name.
 * Subscribes once per app lifecycle — subsequent mounts reuse the cache.
 *
 * @returns {{
 *   districts: Array<{id: string, name: string, [key: string]: any}>,
 *   districtNames: string[],
 *   districtsByName: Object,
 *   loading: boolean,
 *   error: Error|null
 * }}
 */
export function useDistricts() {
  const [districts, setDistricts] = useState(cachedDistricts || []);
  const [loading, setLoading] = useState(cachedDistricts === null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = subscribe((data) => {
      setDistricts(data);
      setLoading(false);
    });
    return unsub;
  }, []);

  const districtNames = districts.map((d) => d.name);

  const districtsByName = districts.reduce((acc, d) => {
    acc[d.name] = d;
    return acc;
  }, {});

  return { districts, districtNames, districtsByName, loading, error };
}
